import React, { useState, useEffect, useContext, useCallback } from 'react';
import { 
  collection, query, where, getDocs, getDoc, doc, 
  writeBatch, updateDoc, setDoc, orderBy, limit, startAfter, deleteField
} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faSearch, faSpinner, faCalendarAlt, 
  faUsers, faExchangeAlt, faCheckCircle, faSuitcase, faArrowDown
} from '@fortawesome/free-solid-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { UserContext } from '../../context/UserContext';
import { TourContext } from '../../context/TourContext';
import styles from './AssignInventory.module.css';
import { createNotification } from '../../utils/NotificationService';


const AssignInventoryModal = ({ isOpen, onClose, onAssign }) => {
  const { userData } = useContext(UserContext);
  const { tours, setSelectedTour } = useContext(TourContext);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('dates');
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Tours state
  const [displayedTours, setDisplayedTours] = useState([]);
  const [selectedTour, setSelectedTourLocal] = useState(null);
  const [hasMoreTours, setHasMoreTours] = useState(false);
  const [isLoadingMoreTours, setIsLoadingMoreTours] = useState(false);
  const [lastTourDoc, setLastTourDoc] = useState(null);
  
  // Date selection state
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  
  // Staff selection state
  const [staffMembers, setStaffMembers] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Initialize data when modal opens
  useEffect(() => {
    if (isOpen && userData) {
      fetchStaffMembers();
      fetchInitialTours();
    }
  }, [isOpen, userData]);
  
  // Fetch staff members
  const fetchStaffMembers = async () => {
    if (!userData?.userId) return;
    
    try {
      const db = getFirestoreInstance();
      
      // Fetch staff from subcollection
      const staffQuery = query(
        collection(db, 'users', userData.userId, 'staff'),
        where('status', '==', 'accepted')
      );
      
      const staffSnapshot = await getDocs(staffQuery);
      
      if (staffSnapshot.empty) {
        setStaffMembers([]);
        return;
      }
      
      const staffData = await Promise.all(
        staffSnapshot.docs.map(async (docSnapshot) => {
          const staffData = docSnapshot.data();
          
          try {
            const userDoc = await getDoc(doc(db, 'users', docSnapshot.id));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              return {
                id: docSnapshot.id,
                fullName: userData.fullName || '',
                userName: userData.userName || '',
                profilePictureUrl: userData.profilePictureUrl || '',
                userSubType: userData.userSubType || '',
                email: userData.email || '',
                role: staffData.role || 'staff'
              };
            }
            return null;
          } catch (err) {
            console.error(`Error fetching staff data for ${docSnapshot.id}:`, err);
            return null;
          }
        })
      );
      
      setStaffMembers(staffData.filter(Boolean));
    } catch (error) {
      console.error('Error fetching staff data:', error);
    }
  };
  
  // Fetch initial tours (latest 4)
  const fetchInitialTours = async () => {
    if (!userData?.userId) return;
    setIsLoading(true);
    
    try {
      const db = getFirestoreInstance();
      
      // Query to get the latest 4 tours from tours subcollection
      const toursQuery = query(
        collection(db, 'users', userData.userId, 'tours'),
        orderBy('createdAt', 'desc'),
        limit(4)
      );
      
      const toursSnapshot = await getDocs(toursQuery);
      
      if (toursSnapshot.empty) {
        setDisplayedTours([]);
        setHasMoreTours(false);
        setIsLoading(false);
        return;
      }
      
      // Extract tour IDs
      const tourIds = toursSnapshot.docs.map(doc => doc.id);
      
      // Get the last document for pagination
      setLastTourDoc(toursSnapshot.docs[toursSnapshot.docs.length - 1]);
      
      // Check if there are potentially more tours
      setHasMoreTours(toursSnapshot.docs.length === 4);
      
      // Fetch full tour details
      const tourDetails = await Promise.all(
        tourIds.map(async (tourId) => {
          const tourDoc = await getDoc(doc(db, 'Tours', tourId));
          if (tourDoc.exists()) {
            return {
              id: tourId,
              ...tourDoc.data()
            };
          }
          return null;
        })
      );
      
      // Filter out null values
      const validTours = tourDetails.filter(Boolean);
      
      setDisplayedTours(validTours);
      
      // Auto-select the first tour if there is one
      if (validTours.length > 0) {
        setSelectedTourLocal(validTours[0]);
        fetchTourDates(validTours[0]);
      }
    } catch (error) {
      console.error('Error fetching tours:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load more tours
  const loadMoreTours = async () => {
    if (!userData?.userId || !lastTourDoc || isLoadingMoreTours) return;
    
    setIsLoadingMoreTours(true);
    
    try {
      const db = getFirestoreInstance();
      
      // Query for the next 4 tours
      const toursQuery = query(
        collection(db, 'users', userData.userId, 'tours'),
        orderBy('createdAt', 'desc'),
        startAfter(lastTourDoc),
        limit(4)
      );
      
      const toursSnapshot = await getDocs(toursQuery);
      
      if (toursSnapshot.empty) {
        setHasMoreTours(false);
        setIsLoadingMoreTours(false);
        return;
      }
      
      // Extract tour IDs
      const tourIds = toursSnapshot.docs.map(doc => doc.id);
      
      // Update last document for pagination
      setLastTourDoc(toursSnapshot.docs[toursSnapshot.docs.length - 1]);
      
      // Check if there are potentially more tours
      setHasMoreTours(toursSnapshot.docs.length === 4);
      
      // Fetch full tour details
      const tourDetails = await Promise.all(
        tourIds.map(async (tourId) => {
          const tourDoc = await getDoc(doc(db, 'Tours', tourId));
          if (tourDoc.exists()) {
            return {
              id: tourId,
              ...tourDoc.data()
            };
          }
          return null;
        })
      );
      
      // Filter out null values and add to existing tours
      const validTours = tourDetails.filter(Boolean);
      
      setDisplayedTours(prev => [...prev, ...validTours]);
    } catch (error) {
      console.error('Error loading more tours:', error);
    } finally {
      setIsLoadingMoreTours(false);
    }
  };
  
  // Fetch tour dates for a selected tour
  const fetchTourDates = async (tour) => {
    if (!tour || !userData?.userId) return;
    
    setIsLoading(true);
    setAvailableDates([]);
    
    try {
      const db = getFirestoreInstance();
      
      // Get the full tour data including vendorDetails
      const tourRef = doc(db, 'Tours', tour.id);
      const tourSnap = await getDoc(tourRef);
      
      if (!tourSnap.exists()) {
        setAvailableDates([]);
        setIsLoading(false);
        return;
      }
      
      const tourData = { id: tour.id, ...tourSnap.data() };
      
      // Find vendor entry in vendorDetails
      const vendorEntry = tourData.vendorDetails?.find(
        vendor => vendor.userId === userData.userId
      );
      
      if (!vendorEntry || !vendorEntry.assignedDates || vendorEntry.assignedDates.length === 0) {
        setAvailableDates([]);
        setIsLoading(false);
        return;
      }
      
      // Get the assigned date IDs for this vendor
      const assignedDateIds = vendorEntry.assignedDates.map(
        date => date.tourDateDocId
      );
      
      // Fetch each date document
      const datesData = await Promise.all(
        assignedDateIds.map(async (dateId) => {
          const dateDoc = await getDoc(doc(db, 'Tours', tour.id, 'tourDates', dateId));
          if (dateDoc.exists()) {
            return {
              id: dateId,
              ...dateDoc.data()
            };
          }
          return null;
        })
      );
      
      // Filter out null values and sort by date
      const validDates = datesData.filter(Boolean).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      
      setAvailableDates(validDates);
    } catch (error) {
      console.error('Error fetching tour dates:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle tour selection
  const handleTourSelect = (tour) => {
    setSelectedTourLocal(tour);
    setSelectedDates([]); // Reset selected dates
    fetchTourDates(tour);
  };
  
  // Handle date selection
  const handleDateSelect = (dateId) => {
    setSelectedDates(prev => {
      if (prev.includes(dateId)) {
        // Remove date if already selected
        return prev.filter(id => id !== dateId);
      } else {
        // Add date if not selected
        return [...prev, dateId];
      }
    });
  };
  
  // Handle staff selection
  const handleStaffSelect = (staffId) => {
    setSelectedStaff(prev => {
      if (prev.includes(staffId)) {
        // Remove staff if already selected
        return prev.filter(id => id !== staffId);
      } else {
        // Add staff if not selected
        return [...prev, staffId];
      }
    });
  };
  
  // Handle inventory search
  const handleSearch = async () => {
    if (!searchTerm.trim() || !userData?.userId) return;
    
    setIsSearching(true);
    
    try {
      const db = getFirestoreInstance();
      const searchTermLower = searchTerm.toLowerCase();
      
      // Search across all inventory types
      const inventoryQuery = query(
        collection(db, 'users', userData.userId, 'inventoryGroups')
      );
      
      const querySnapshot = await getDocs(inventoryQuery);
      
      // Filter results client-side for keywords
      const results = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(item => {
          // Check if any keyword contains the search term
          return item.keywords?.some(keyword => 
            keyword.includes(searchTermLower)
          ) || 
          item.name?.toLowerCase().includes(searchTermLower) ||
          item.brand?.toLowerCase().includes(searchTermLower) ||
          item.model?.toLowerCase().includes(searchTermLower) ||
          item.type?.toLowerCase().includes(searchTermLower);
        })
        .filter(item => {
          // Only show items with available quantity > 0
          return (item.availableQuantity || 0) > 0;
        });
      
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching inventory:', error);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Toggle item selection
  const handleItemSelect = (item) => {
    setSelectedItems(prev => {
      const isSelected = prev.some(i => i.id === item.id);
      if (isSelected) {
        // Remove item if already selected
        return prev.filter(i => i.id !== item.id);
      } else {
        // Add item with quantity 1 if not selected
        return [...prev, { ...item, assignQuantity: 1 }];
      }
    });
  };
  
  // Update item quantity
  const updateItemQuantity = (itemId, quantity) => {
    setSelectedItems(prev => 
      prev.map(item => 
        item.id === itemId 
          ? { ...item, assignQuantity: Math.min(quantity, item.availableQuantity) } 
          : item
      )
    );
  };
  
  // Submit assignment
  const handleSubmit = async () => {
    if (!selectedTour || selectedDates.length === 0 || selectedItems.length === 0) {
      alert('Please select a tour, dates, and inventory items.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const db = getFirestoreInstance();
      const batch = writeBatch(db);
      
      // Get tour dates info
      const tourDatesInfo = await Promise.all(
        selectedDates.map(async (dateId) => {
          const dateDoc = await getDoc(doc(db, 'Tours', selectedTour.id, 'tourDates', dateId));
          return {
            id: dateId,
            ...dateDoc.data()
          };
        })
      );
      
      // Get all dates in range if using 'tour' tab
      let allDatesInRange = tourDatesInfo;
      
      if (activeTab === 'tour' && selectedDates.length >= 2) {
        // Sort dates chronologically
        const sortedDates = [...tourDatesInfo].sort((a, b) => 
          new Date(a.date) - new Date(b.date)
        );
        
        const startDate = new Date(sortedDates[0].date);
        const endDate = new Date(sortedDates[sortedDates.length - 1].date);
        
        // Find all dates in the range that belong to this tour
        const datesInRangeQuery = query(
          collection(db, 'Tours', selectedTour.id, 'tourDates')
        );
        
        const datesSnapshot = await getDocs(datesInRangeQuery);
        
        allDatesInRange = datesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(date => {
            const dateObj = new Date(date.date);
            return dateObj >= startDate && dateObj <= endDate;
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));
      }
      
      // Get tour document
      const tourRef = doc(db, 'Tours', selectedTour.id);
      const tourDoc = await getDoc(tourRef);
      
      if (!tourDoc.exists()) {
        throw new Error('Tour document not found.');
      }
      
      const tourData = tourDoc.data();
      
      // Handle staff invitations (CHANGED: Send invitations instead of direct assignment)
      if (selectedStaff.length > 0) {
        // For each selected staff member, send an invitation notification
        for (const staffId of selectedStaff) {
          const staffMember = staffMembers.find(s => s.id === staffId);
          
          if (staffMember) {
            // Create staff invitation notification
            const staffInvitationData = {
              type: 'STAFF_REQUEST',
              title: 'Staff Assignment Request',
              message: `You've been requested to work on ${selectedTour.tourName} by ${userData.fullName}`,
              tourId: selectedTour.id,
              tourName: selectedTour.tourName,
              organizationId: userData.userId,
              organizationName: userData.fullName,
              organizationType: userData.userType,
              userId: staffId,
              userName: staffMember.userName,
              fullName: staffMember.fullName,
              email: staffMember.email,
              profilePictureUrl: staffMember.profilePictureUrl,
              assignedDates: allDatesInRange.map(date => ({
                tourDateDocId: date.id,
                date: date.date
              })),
              permissions: {
                canAddItems: true,
                canEditItems: true,
                canDeleteItems: false,
                canChangeStatus: true
              },
              status: 'pending',
              timestamp: new Date().toISOString(),
              createdBy: {
                userId: userData.userId,
                fullName: userData.fullName,
                userName: userData.userName,
                email: userData.email,
                profilePictureUrl: userData.profilePictureUrl,
                userType: userData.userType,
                userSubType: userData.userSubType
              }
            };
            
            // Send invitation to staff member (CHANGED: Use 'invitations' instead of 'general')
            await createNotification(staffId, 'invitations', staffInvitationData);
          }
        }
        
        // Note: Staff will only be added to tour documents after they accept the invitation
        // The acceptance is handled by acceptStaffRequest function in NotificationService.js
      }
      
      // Process each selected inventory item
      for (const item of selectedItems) {
        // Get individual items from this group that are available
        const itemsQuery = query(
          collection(db, 'users', userData.userId, 'inventoryItems'),
          where('groupId', '==', item.id),
          where('status', '==', 'Available')
        );
        
        const itemsSnapshot = await getDocs(itemsQuery);
        
        // Check if we have enough available items
        if (itemsSnapshot.docs.length < item.assignQuantity) {
          throw new Error(`Not enough available items for ${item.name}.`);
        }
        
        // Select the required number of items to assign
        const itemsToAssign = itemsSnapshot.docs.slice(0, item.assignQuantity);
        
        // Track the IDs of assigned items for the inventoryGroups document
        const assignedItemIds = itemsToAssign.map(itemDoc => itemDoc.id);
        
        // Update each individual item
        for (const itemDoc of itemsToAssign) {
          const itemId = itemDoc.id;
          const itemRef = doc(db, 'users', userData.userId, 'inventoryItems', itemId);
          
          // Update status to Reserved
          batch.update(itemRef, {
            status: 'Reserved',
            updatedAt: new Date().toISOString(),
            assignedTourIds: {
              [selectedTour.id]: {
                tourId: selectedTour.id,
                tourName: selectedTour.tourName || '',
                actName: selectedTour.actDetails?.name || '',
                assignedDates: allDatesInRange.map(date => ({
                  tourDateDocId: date.id,
                  date: date.date,
                })),
                assignedBy: userData.userId,
                assignedStaff: selectedStaff,
                assignedAt: new Date().toISOString()
              }
            }
          });
        }
        
        // For each tour date, create documents in assignedInventoryItems and assignedInventoryGroups subcollections
        for (const date of allDatesInRange) {
          const tourDateRef = doc(db, 'Tours', selectedTour.id, 'tourDates', date.id);
          
          // Get existing data for this date
          const tourDateDoc = await getDoc(tourDateRef);
          if (!tourDateDoc.exists()) continue;
          
          // For each individual item being assigned
          for (const itemDoc of itemsToAssign) {
            const itemId = itemDoc.id;
            const itemData = itemDoc.data();
            
            // Create a reference to the assignedInventoryItems subcollection
            const assignedItemRef = doc(db, 'Tours', selectedTour.id, 'tourDates', date.id, 'assignedInventoryItems', itemId);
            
            // Add this item to assignedInventoryItems subcollection
            batch.set(assignedItemRef, {
              id: itemId,
              name: item.name,
              brand: item.brand,
              model: itemData.model || item.model || '',
              serialNumber: itemData.serialNumber || '',
              type: item.type,
              subType: item.subType,
              rentPerDay: item.rentPerDay,
              groupId: item.id,
              vendorId: userData.userId,
              assignedBy: userData.userId,
              assignedStaff: selectedStaff,
              assignedAt: new Date().toISOString()
            });
          }
          
          // Create a reference to the assignedInventoryGroups subcollection (using group ID as document ID)
          const assignedGroupRef = doc(db, 'Tours', selectedTour.id, 'tourDates', date.id, 'assignedInventoryGroups', item.id);
          
          // Add group info to assignedInventoryGroups subcollection
          batch.set(assignedGroupRef, {
            brand: item.brand,
            coverPhotoUrl: item.coverPhotoUrl || null,
            description: item.description || '',
            fileUrl: item.fileUrl || null,
            groupId: item.id,
            inventoryType: item.inventoryType || '',
            keywords: item.keywords || [],
            model: item.model || '',
            name: item.name,
            totalQuantity: item.assignQuantity, // Track how many were assigned from this group
            rentPerDay: item.rentPerDay,
            subType: item.subType || '',
            type: item.type || '',
            updatedAt: new Date().toISOString(),
            vendorId: userData.userId
          });
          
          // Add a flag to indicate we're using subcollections and remove old fields
          batch.update(tourDateRef, {
            inventoryStoredAsSubcollections: true,
            // Using FieldValue.delete() to remove old fields if they exist
            assignedInventoryItems: deleteField(),
            assignedInventoryGroups: deleteField()
          });
        }
        
        // Update inventory group counts and store assigned item IDs
        const groupRef = doc(db, 'users', userData.userId, 'inventoryGroups', item.id);
        
        // Get current counts
        const groupDoc = await getDoc(groupRef);
        if (groupDoc.exists()) {
          const currentCounts = groupDoc.data();
          
          // Calculate new counts
          const newAvailable = Math.max(0, (currentCounts.availableQuantity || 0) - item.assignQuantity);
          const newReserved = (currentCounts.reservedQuantity || 0) + item.assignQuantity;
          
          // Get current assignedTourIds or initialize if it doesn't exist
          const currentAssignedTourIds = currentCounts.assignedTourIds || {};
          
          // Update or create the tour entry
          const updatedAssignedTourIds = {
            ...currentAssignedTourIds,
            [selectedTour.id]: {
              tourId: selectedTour.id,
              tourName: selectedTour.tourName || '',
              actName: selectedTour.actDetails?.name || '',
              assignedDates: allDatesInRange.map(date => ({
                tourDateDocId: date.id,
                date: date.date,
              })),
              assignedItems: currentAssignedTourIds[selectedTour.id]?.assignedItems
                ? [...new Set([...currentAssignedTourIds[selectedTour.id].assignedItems, ...assignedItemIds])]
                : assignedItemIds,
              assignedStaff: selectedStaff,
              assignedBy: userData.userId,
              assignedAt: new Date().toISOString()
            }
          };
          
          // Update group document
          batch.update(groupRef, {
            availableQuantity: newAvailable,
            reservedQuantity: newReserved,
            assignedTourIds: updatedAssignedTourIds, // Include assigned item IDs here
            updatedAt: new Date().toISOString()
          });
        }
      }
      
      // Check if vendorDetails needs to be updated in the tour document
      if (!tourData.vendorDetails) {
        tourData.vendorDetails = [];
      }
      
      // Check if vendor is already in vendorDetails
      const vendorIndex = tourData.vendorDetails.findIndex(
        vendor => vendor.userId === userData.userId
      );
      
      const assignedDatesForVendor = allDatesInRange.map(date => ({
        tourDateDocId: date.id,
        date: date.date
      }));
      
      if (vendorIndex !== -1) {
        // Vendor already exists, update assignedDates
        const existingAssignedDates = tourData.vendorDetails[vendorIndex].assignedDates || [];
        
        // Merge existing dates with new dates
        const uniqueDates = new Map();
        
        // Add existing dates to map
        existingAssignedDates.forEach(date => {
          uniqueDates.set(date.tourDateDocId, date);
        });
        
        // Add new dates to map
        assignedDatesForVendor.forEach(date => {
          uniqueDates.set(date.tourDateDocId, date);
        });
        
        // Convert back to array
        const mergedDates = Array.from(uniqueDates.values());
        
        // Update in vendorDetails array
        tourData.vendorDetails[vendorIndex].assignedDates = mergedDates;
      } else {
        // Vendor not yet in vendorDetails, add them
        tourData.vendorDetails.push({
          userId: userData.userId,
          assignedDates: assignedDatesForVendor
        });
      }
      
      // Update vendorDetails in the tour document
      batch.update(tourRef, {
        vendorDetails: tourData.vendorDetails
      });
      
      // Commit batch
      await batch.commit();
      
      // Notify parent component
      if (onAssign) {
        onAssign({
          tourId: selectedTour.id,
          dates: allDatesInRange,
          items: selectedItems,
          staff: selectedStaff
        });
      }
      
      // Close modal
      onClose();
      
    } catch (error) {
      console.error('Error assigning inventory:', error);
      alert(`Failed to assign inventory: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };
  
  if (!isOpen) return null;
  
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Assign Inventory</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className={styles.modalTabs}>
          <button 
            className={`${styles.tabButton} ${activeTab === 'dates' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('dates')}
          >
            <FontAwesomeIcon icon={faCalendarAlt} />
            <span>Specific Dates</span>
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'tour' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('tour')}
          >
            <FontAwesomeIcon icon={faSuitcase} />
            <span>Full Tour</span>
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {/* Inventory Search Section */}
          <div className={styles.searchSection}>
            <h3 className={styles.sectionTitle}>Search Inventory</h3>
            <div className={styles.searchBar}>
              <input
                type="text"
                placeholder="Search inventory items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                className={styles.searchInput}
              />
              <button 
                className={styles.searchButton}
                onClick={handleSearch}
                disabled={isSearching}
              >
                {isSearching ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faSearch} />
                )}
              </button>
            </div>
            
            <div className={styles.searchResults}>
              {isSearching ? (
                <div className={styles.loadingContainer}>
                  <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
                  <p>Searching inventory...</p>
                </div>
              ) : searchResults.length > 0 ? (
                <div className={styles.resultsList}>
                  {searchResults.map(item => (
                    <div 
                      key={item.id}
                      className={`${styles.resultItem} ${
                        selectedItems.some(i => i.id === item.id) ? styles.selectedItem : ''
                      }`}
                      onClick={() => handleItemSelect(item)}
                    >
                      <div className={styles.itemImageContainer}>
                        {item.coverPhotoUrl ? (
                          <img 
                            src={item.coverPhotoUrl} 
                            alt={item.name} 
                            className={styles.itemImage}
                          />
                        ) : (
                          <div className={styles.defaultImage}>
                            <span>{item.name.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                      <div className={styles.itemInfo}>
                        <h4 className={styles.itemName}>{item.name}</h4>
                        <p className={styles.itemDetails}>
                          {item.brand} {item.model && `- ${item.model}`}
                        </p>
                        <p className={styles.itemAvailability}>
                          Available: {item.availableQuantity || 0} of {item.totalQuantity || 0}
                        </p>
                      </div>
                      {selectedItems.some(i => i.id === item.id) && (
                        <div className={styles.checkIcon}>
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : searchTerm ? (
                <div className={styles.noResults}>
                  No inventory items found matching your search.
                </div>
              ) : (
                <div className={styles.searchPrompt}>
                  Search for inventory items to assign.
                </div>
              )}
            </div>
          </div>
          
          {/* Selected Items Section */}
          {selectedItems.length > 0 && (
            <div className={styles.selectedItemsSection}>
              <h3 className={styles.sectionTitle}>Selected Items</h3>
              <div className={styles.selectedItemsList}>
                {selectedItems.map(item => (
                  <div key={item.id} className={styles.selectedItemRow}>
                    <div className={styles.selectedItemInfo}>
                      <h4 className={styles.selectedItemName}>{item.name}</h4>
                      <p className={styles.selectedItemDetails}>
                        {item.brand} {item.model && `- ${item.model}`}
                      </p>
                    </div>
                    <div className={styles.quantitySelector}>
                      <button 
                        className={styles.quantityButton}
                        onClick={() => updateItemQuantity(item.id, Math.max(1, item.assignQuantity - 1))}
                        disabled={item.assignQuantity <= 1}
                      >
                        -
                      </button>
                      <span className={styles.quantityValue}>{item.assignQuantity}</span>
                      <button 
                        className={styles.quantityButton}
                        onClick={() => updateItemQuantity(item.id, item.assignQuantity + 1)}
                        disabled={item.assignQuantity >= item.availableQuantity}
                      >
                        +
                      </button>
                    </div>
                    <button 
                      className={styles.removeButton}
                      onClick={() => handleItemSelect(item)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Tour Selection Section */}
          <div className={styles.tourSection}>
            <h3 className={styles.sectionTitle}>Select Tour</h3>
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
                <p>Loading tours...</p>
              </div>
            ) : displayedTours.length > 0 ? (
              <div className={styles.toursList}>
                {displayedTours.map(tour => (
                  <div 
                    key={tour.id}
                    className={`${styles.tourItem} ${
                      selectedTour?.id === tour.id ? styles.selectedTour : ''
                    }`}
                    onClick={() => handleTourSelect(tour)}
                  >
                    <h4 className={styles.tourName}>{tour.tourName}</h4>
                    <p className={styles.tourDetails}>
                      {tour.actDetails?.name && `${tour.actDetails.name}`}
                    </p>
                    {selectedTour?.id === tour.id && (
                      <div className={styles.checkIcon}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Load More Button */}
                {hasMoreTours && (
                  <button 
                    className={styles.loadMoreButton}
                    onClick={loadMoreTours}
                    disabled={isLoadingMoreTours}
                  >
                    {isLoadingMoreTours ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faArrowDown} />
                        <span>Load More</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.noTours}>
                <p>No tours available for assignment.</p>
              </div>
            )}
          </div>
          
          {/* Date Selection Section */}
          {selectedTour && (
            <div className={styles.dateSection}>
              <h3 className={styles.sectionTitle}>
                {activeTab === 'dates' ? 'Select Specific Dates' : 'Select Tour Range'}
              </h3>
              {isLoading ? (
                <div className={styles.loadingContainer}>
                  <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
                  <p>Loading dates...</p>
                </div>
              ) : availableDates.length > 0 ? (
                <div className={styles.datesList}>
                  {availableDates.map(date => (
                    <div 
                      key={date.id}
                      className={`${styles.dateItem} ${
                        selectedDates.includes(date.id) ? styles.selectedDate : ''
                      }`}
                      onClick={() => handleDateSelect(date.id)}
                    >
                      <div className={styles.dateInfo}>
                        <span className={styles.dateValue}>
                          {formatDate(date.date)}
                        </span>
                        <div className={styles.venueContainer}>
                          <span className={styles.venueName}>
                            {date.event?.venueDetails?.venueData?.displayName || 'No venue specified'}
                          </span>
                          <span className={styles.venueAddress}>
                            {date.event?.venueDetails?.venueData?.formattedAddress || 'No address specified'}
                          </span>
                        </div>
                      </div>
                      {selectedDates.includes(date.id) && (
                        <div className={styles.checkIcon}>
                          <FontAwesomeIcon icon={faCheckCircle} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.noDates}>
                  <p>No dates available for this tour or you're not assigned to any dates.</p>
                </div>
              )}
              {activeTab === 'tour' && selectedDates.length > 0 && (
                <div className={styles.rangeInfo}>
                  <p>
                    <FontAwesomeIcon icon={faExchangeAlt} className={styles.rangeIcon} />
                    {selectedDates.length === 1 ? (
                      'Select at least one more date to create a range'
                    ) : (
                      `Selected ${selectedDates.length} dates in range`
                    )}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Staff Assignment Section */}
          <div className={styles.staffSection}>
            <h3 className={styles.sectionTitle}>Assign Staff (Optional)</h3>
            {staffMembers.length > 0 ? (
              <div className={styles.staffList}>
                {staffMembers.map(staff => (
                  <div 
                    key={staff.id}
                    className={`${styles.staffItem} ${
                      selectedStaff.includes(staff.id) ? styles.selectedStaff : ''
                    }`}
                    onClick={() => handleStaffSelect(staff.id)}
                  >
                    <div className={styles.staffImageContainer}>
                      {staff.profilePictureUrl ? (
                        <img 
                          src={staff.profilePictureUrl} 
                          alt={staff.fullName} 
                          className={styles.staffImage}
                        />
                      ) : (
                        <div className={styles.defaultStaffImage}>
                          <FontAwesomeIcon icon={faUsers} />
                        </div>
                      )}
                    </div>
                    <div className={styles.staffInfo}>
                      <h4 className={styles.staffName}>{staff.fullName}</h4>
                      <p className={styles.staffDetails}>
                        @{staff.userName} • {staff.userSubType}
                      </p>
                    </div>
                    {selectedStaff.includes(staff.id) && (
                      <div className={styles.checkIcon}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.noStaff}>
                <p>No staff members available. Add staff in Staff Management.</p>
              </div>
            )}
          </div>
        </div>
        
        <div className={styles.modalFooter}>
          <button 
            className={styles.cancelButton}
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button 
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={
              isSubmitting || 
              selectedItems.length === 0 || 
              selectedDates.length === 0 || 
              !selectedTour
            }
          >
            {isSubmitting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Assigning...</span>
              </>
            ) : (
              <span>Assign Inventory</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignInventoryModal;